import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { UserData } from "@sportyano/core/models/authentications/login-data.model";
import { IResponse } from "@sportyano/core/models/response/response.interface";
import {
	USER_TYPE,
	USERS_TYPE,
	UsersType,
	UserType,
} from "@sportyano/core/models/user-type/user-type";
import { AuthService } from "@sportyano/core/services/authServices/auth.service";
import { filter, map, Observable, of, switchMap } from "rxjs";
import { tap } from "rxjs/internal/operators/tap";
import { environment } from "src/environments/environment";

@Injectable({
	providedIn: "root",
})
export class AccountService {
	private _HttpClient: HttpClient = inject(HttpClient);
	private _authService: AuthService = inject(AuthService);

	public getAccountData(
		userType: UsersType
	  ): Observable<Omit<IResponse<UserData>, "data">> {
		if (!this._authService.getAuthenticationStateSync() && (userType as string) !== "cafe") {
			return of({ records: null } as any);
		}
		if ((userType as string) === "cafe") {
			const saved = localStorage.getItem("sportyano_cafe_user");
			let cafeData: any = {};
			if (saved) {
				try { cafeData = JSON.parse(saved); } catch {}
			}
			const cafeId = cafeData?.id || (this._authService.getUserData() as any)?.id;
			if (cafeId) {
				return this._HttpClient.get<any>(`${environment.baseUrl}cafes/${cafeId}`).pipe(
					map((res: any) => {
						const cafe = res?.data || cafeData;
						const records: any = {
							id: cafe.id,
							name_ar: cafe.name_ar,
							name_en: cafe.name_en,
							phone: cafe.phone,
							email: cafe.email,
							is_phone_verified: true,
							is_email_verified: cafe.is_email_verified ?? !!cafe.email,
							is_social: false,
							status: 1,
						};
						return { records } as any;
					}),
					tap((t: any) => {
						this._authService.setUserData = t?.records ?? null;
					})
				);
			} else {
				const records: any = {
					name_ar: cafeData.name_ar,
					name_en: cafeData.name_en,
					phone: cafeData.phone,
					email: cafeData.email,
					is_phone_verified: true,
					is_email_verified: !!cafeData.email,
					is_social: false,
					status: 1,
				};
				return of({ records } as any);
			}
		}
		return of(this._userTypeToUsersType(userType)).pipe(
		  filter((f) => !!f),
		  switchMap((usersType) => {
			if(usersType === USER_TYPE.branch){
				usersType = "branch-user";
			}
			return this._HttpClient.get<Omit<IResponse<UserData>, "data">>(
			  `${environment.baseUrl + usersType}`
			);
		  }),
		  map((response) => {
			if (response?.records) {
			  const phone = response.records.phone;
			  // Check if phone contains only numbers
			  const isValidPhone = /^\d+$/.test(phone!);
			  response.records.phone = isValidPhone ? phone : null;
			}
			return response;
		  }),
		  tap((t) => {
			// Store user data
			this._authService.setUserData = t?.records ?? null;
		  })
		);
	  }


	private _userTypeToUsersType(userType: UserType): UsersType {
		switch (userType) {
			case USER_TYPE.player:
				return USERS_TYPE.players;
			case USER_TYPE.academy:
				return USERS_TYPE.academies;
			case USER_TYPE.playground:
				return USERS_TYPE.playgrounds;
			case USER_TYPE.viewer:
				return USERS_TYPE.viewers;
			case USER_TYPE.scouting:
				return USERS_TYPE.scoutings;
			case USER_TYPE.coach:
				return USERS_TYPE.coaches;
			case USER_TYPE.federation:
				return USERS_TYPE.federations;
			default:
				return userType;
		}
	}

	public requestPasswordForPhoneChange(): Observable<any> {
		return this._HttpClient.get(
			`${environment.baseUrl}email-addresses/send-password`
		);
	}

	public deleteAccount(): Observable<any> {
		return this._HttpClient.delete(
			`${environment.baseUrl}delete-account`
		);
	}
}
